
import { Flight } from '@/types/flight';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FlightStatusBadge } from '@/components/FlightStatusBadge';
import { formatTime } from '@/lib/formatters';
import { Edit } from 'lucide-react';
import { format } from 'date-fns';

interface SearchResultsTableProps {
  flights: Flight[];
  onSelectFlight: (flight: Flight) => void;
  loading?: boolean;
}

export function SearchResultsTable({ flights, onSelectFlight, loading = false }: SearchResultsTableProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle className="flex items-center gap-2">
            <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
            Search Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (flights.length === 0) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle className="flex items-center gap-2">
            <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
            Search Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No flights found matching your search criteria.</p>
            <p className="text-sm">Try adjusting your filters or search terms.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
          Search Results ({flights.length} flight{flights.length !== 1 ? 's' : ''})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flight</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Departure</TableHead>
                <TableHead>Arrival</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Gate</TableHead>
                <TableHead className="text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flights.map((flight) => (
                <TableRow key={flight.id} className="hover:bg-muted/50">
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-semibold">{flight.flight_number}</span>
                      <span className="text-xs text-muted-foreground">
                        {flight.organization?.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {format(new Date(flight.scheduled_departure), 'MMM dd, yyyy')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {flight.departure_city?.code} → {flight.arrival_city?.code}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {flight.departure_city?.name} to {flight.arrival_city?.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-sm">
                      {formatTime(flight.scheduled_departure)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-sm">
                      {formatTime(flight.scheduled_arrival)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <FlightStatusBadge 
                      status={flight.status} 
                      pulsing={flight.status !== "On Time"}
                      showActualStatus={true}
                    />
                    {flight.status_reason && (
                      <div className="text-xs text-muted-foreground mt-1 max-w-32 truncate">
                        {flight.status_reason}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{flight.gate || 'TBA'}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      size="sm"
                      onClick={() => onSelectFlight(flight)}
                      className="flex items-center gap-1"
                    >
                      <Edit className="h-3 w-3" />
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
